import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import BlockIcon from "@mui/icons-material/Block";
import CameraIcon from "../../../shared/assets/icons/CameraIcon";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getDecommissionPreview, decommissionMachine, getMachines, getDecommissionedMachines, setSuccess } from "../customerMachinerySlice";
import { CustomerMachineryState, Machine } from "../models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import { dt } from "../utils/dt";

interface DecommissionDialogProps {
  open: boolean;
  machine: Machine | null;
  onClose: () => void;
  onDecommissioned: () => void;
}

const DecommissionDialog = ({ open, machine, onClose, onDecommissioned }: DecommissionDialogProps) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const isPhone = useMediaQuery("(max-width:768px)");

  const { decommissionPreview, decommissionPreviewLoading } =
    useAppSelector<CustomerMachineryState>((state: any) => state.customerMachinery);

  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch preview when dialog opens
  useEffect(() => {
    if (open && machine) {
      dispatch(getDecommissionPreview(machine.id));
      setReason("");
      setSubmitting(false);
    }
  }, [open, machine]);

  if (!machine) return null;

  const displayName = getMachineDisplayName(machine);
  const imageUrl = machine?.image?.content
    || machine?.catalogModel?.imageStandardUrl
    || machine?.catalogModel?.imageUrl
    || null;

  const preview = decommissionPreview;
  const rfs = preview?.rfsSummary;
  const hasActiveRfs = rfs ? rfs.totalActiveCount > 0 : false;
  const isBlocked = rfs ? rfs.hasBlockingRequests : false;
  const canDecommission = preview ? preview.canDecommission : true;

  const handleConfirm = async () => {
    if (!machine || isBlocked) return;
    setSubmitting(true);
    try {
      await dispatch(
        decommissionMachine({
          id: machine.id,
          reason: reason.trim() || undefined,
          forceArchiveRfs: hasActiveRfs ? true : undefined,
        })
      ).unwrap();
      dispatch(setSuccess(translate("machineDecommissioned")));
      onDecommissioned();
    } catch (err) {
      // Error handled by slice
    } finally {
      setSubmitting(false);
    }
  };

  // Tier color -- amber for no RFS, red-amber for active RFS, red for blocked
  const tierColor = isBlocked
    ? "#EF4444"
    : hasActiveRfs
    ? dt.decommissioned.amberDark
    : dt.decommissioned.amber;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isPhone}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isPhone ? 0 : dt.radius,
          fontFamily: dt.font,
          overflow: "hidden",
        },
      }}
    >
      {/* Title bar */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: dt.font,
          fontWeight: 600,
          fontSize: "18px",
          color: dt.textPrimary,
          borderBottom: `1px solid #F3F4F6`,
          py: "14px",
          px: "24px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <WarningAmberOutlinedIcon sx={{ color: tierColor, fontSize: "22px" }} />
          {translate("decommissionMachine")}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: dt.textMuted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: "24px", pt: "20px !important", pb: "12px" }}>
        {/* Loading state */}
        {decommissionPreviewLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} sx={{ color: dt.decommissioned.amber }} />
          </Box>
        )}

        {!decommissionPreviewLoading && (
          <>
            {/* Machine preview */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                mb: "20px",
                p: "14px",
                backgroundColor: "#F9FAFB",
                borderRadius: dt.radiusSm,
                border: "1px solid #F3F4F6",
              }}
            >
              <Box
                sx={{
                  width: "64px",
                  height: "64px",
                  minWidth: "64px",
                  borderRadius: "10px",
                  border: "1px solid #EEEEEF",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#F8FAFC",
                }}
              >
                {imageUrl ? (
                  <img src={imageUrl} alt={displayName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <Box sx={{ transform: "scale(0.7)" }}><CameraIcon /></Box>
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    fontFamily: dt.font,
                    color: dt.textPrimary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </Box>
                {machine.serialNumber && (
                  <Box sx={{ fontSize: "13px", fontFamily: dt.fontMono, color: dt.textSecondary, mt: "2px" }}>
                    S/N {machine.serialNumber}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Tier 1: Simple warning (no active RFS) */}
            {!hasActiveRfs && !isBlocked && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  p: "14px",
                  backgroundColor: dt.decommissioned.warning.bg,
                  border: `1px solid ${dt.decommissioned.warning.border}`,
                  borderRadius: dt.radiusSm,
                  mb: "20px",
                }}
              >
                <WarningAmberOutlinedIcon sx={{ color: dt.decommissioned.amber, fontSize: "20px", mt: "1px", flexShrink: 0 }} />
                <Box sx={{ fontSize: "13px", fontFamily: dt.font, color: dt.decommissioned.warning.text, lineHeight: 1.5 }}>
                  {translate("decommissionWarning")}
                </Box>
              </Box>
            )}

            {/* Tier 2: RFS impact listing */}
            {hasActiveRfs && !isBlocked && rfs && (
              <Box
                sx={{
                  p: "14px",
                  backgroundColor: "rgba(239, 68, 68, 0.04)",
                  border: "1px solid rgba(239, 68, 68, 0.15)",
                  borderRadius: dt.radiusSm,
                  mb: "20px",
                }}
              >
                <Box sx={{ fontSize: "13px", fontWeight: 600, fontFamily: dt.font, color: "#B91C1C", mb: "10px" }}>
                  {translate("decommissionImpactTitle")}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {rfs.draftCount > 0 && (
                    <ImpactRow
                      icon={<DeleteSweepOutlinedIcon sx={{ fontSize: "18px", color: "#EF4444" }} />}
                      text={translate("decommissionDraftsWillBeDeleted", { count: rfs.draftCount })}
                      color="#EF4444"
                    />
                  )}
                  {rfs.openNoOffersCount > 0 && (
                    <ImpactRow
                      icon={<ArchiveOutlinedIcon sx={{ fontSize: "18px", color: dt.decommissioned.amberDark }} />}
                      text={translate("decommissionRequestsWillBeArchived", { count: rfs.openNoOffersCount })}
                      color={dt.decommissioned.amberDark}
                    />
                  )}
                  {rfs.negotiatingCount > 0 && (
                    <ImpactRow
                      icon={<GavelOutlinedIcon sx={{ fontSize: "18px", color: "#EF4444" }} />}
                      text={translate("decommissionOffersWillBeWithdrawn", { count: rfs.negotiatingCount })}
                      color="#EF4444"
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Blocked state: InProgress SRs */}
            {isBlocked && rfs && (
              <Box
                sx={{
                  p: "14px",
                  backgroundColor: "rgba(239, 68, 68, 0.06)",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                  borderRadius: dt.radiusSm,
                  mb: "20px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "10px" }}>
                  <BlockIcon sx={{ fontSize: "20px", color: "#EF4444" }} />
                  <Box sx={{ fontSize: "14px", fontWeight: 600, fontFamily: dt.font, color: "#B91C1C" }}>
                    {translate("decommissionBlocked")}
                  </Box>
                </Box>
                <Box sx={{ fontSize: "13px", fontFamily: dt.font, color: "#92400E", lineHeight: 1.5, mb: "10px" }}>
                  {translate("decommissionBlockedDetail")}
                </Box>
                {rfs.blockingRequests.map((sr) => (
                  <Box
                    key={sr.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      p: "8px 10px",
                      backgroundColor: "#FFFFFF",
                      borderRadius: "6px",
                      border: "1px solid #FCA5A5",
                      mb: "4px",
                    }}
                  >
                    <ErrorOutlineIcon sx={{ fontSize: "16px", color: "#EF4444" }} />
                    <a
                      href={`/service-board/${sr.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: "13px",
                        fontFamily: dt.fontMono,
                        fontWeight: 500,
                        color: "#1976d2",
                        textDecoration: "underline",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      #{sr.internalNumber}
                    </a>
                    <Box sx={{ fontSize: "12px", fontFamily: dt.font, color: dt.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {sr.issue}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Reason field -- always shown unless blocked */}
            {!isBlocked && (
              <TextField
                label={translate("decommissionReason")}
                placeholder={translate("decommissionReasonPlaceholder")}
                multiline
                minRows={2}
                maxRows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                fullWidth
                inputProps={{ maxLength: 500 }}
                sx={{
                  mb: "8px",
                  "& .MuiOutlinedInput-root": {
                    fontFamily: dt.font,
                    fontSize: "14px",
                    borderRadius: dt.radiusSm,
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: dt.font,
                    fontSize: "14px",
                  },
                }}
              />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: "24px",
          py: "16px",
          borderTop: "1px solid #F3F4F6",
          gap: "8px",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontFamily: dt.font,
            fontWeight: 500,
            fontSize: "14px",
            borderRadius: dt.radiusSm,
            color: dt.textSecondary,
            borderColor: "#D1D5DB",
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          {tCommon("buttons.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isBlocked || submitting || decommissionPreviewLoading}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            textTransform: "none",
            fontFamily: dt.font,
            fontWeight: 600,
            fontSize: "14px",
            borderRadius: dt.radiusSm,
            backgroundColor: isBlocked ? "#D1D5DB" : tierColor,
            color: "#FFFFFF",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: isBlocked ? "#D1D5DB" : dt.decommissioned.amberDark,
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: "#D1D5DB",
              color: "#9CA3AF",
            },
          }}
        >
          {translate("decommissionConfirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Impact row helper ---
const ImpactRow = ({ icon, text, color }: { icon: React.ReactNode; text: string; color: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
    {icon}
    <Box sx={{ fontSize: "13px", fontFamily: 'var(--font-sans)', color: color, fontWeight: 500, lineHeight: 1.4 }}>
      {text}
    </Box>
  </Box>
);

export default DecommissionDialog;
